/**
 * Maps transfer context QuestionnaireResponses to Medical Record clinical notes.
 */
import type { EpisodeOfCare, QuestionnaireResponse, ServiceRequest } from '@medplum/fhirtypes';
import { getTrackerStage } from '@unify/fhir-model/resources/service-request';
import type { ClinicalNote } from '../pages/clients/data/types';
import {
  TRANSFER_REASON_DISPLAY,
  isTransferReasonCode,
} from './transferFhirConstants';
import { extractServiceRequestPreferences, extractTransferQrItems } from './transferService';

export interface TransferChartNoteContext {
  qr: QuestionnaireResponse;
  serviceRequest?: ServiceRequest;
  episode?: EpisodeOfCare;
  episodeDisplay?: string;
}

interface TransferOutcome {
  statusLabel: string;
  isComplete: boolean;
  assignedProvider?: string;
  completedOn?: string;
}

function formatChartDate(iso: string | undefined): string | undefined {
  if (!iso?.trim()) return undefined;
  try {
    return new Date(iso).toLocaleDateString('en-US', { dateStyle: 'medium' });
  } catch {
    return iso.slice(0, 10);
  }
}

function practitionerIdFromRef(ref: string | undefined): string | undefined {
  if (!ref?.startsWith('Practitioner/')) return undefined;
  return ref.slice('Practitioner/'.length);
}

function reasonLabel(code: string): string {
  return isTransferReasonCode(code) ? TRANSFER_REASON_DISPLAY[code] : code;
}

function resolveTransferOutcome(context: TransferChartNoteContext): TransferOutcome {
  const stage = context.serviceRequest ? getTrackerStage(context.serviceRequest) : undefined;
  const authorRef = context.qr.author?.reference;
  const careManagerRef = context.episode?.careManager?.reference;
  const authorId = practitionerIdFromRef(authorRef);
  const careManagerId = practitionerIdFromRef(careManagerRef);
  const careManagerChanged =
    !!authorId && !!careManagerId && authorId !== careManagerId;

  const assignedProvider = careManagerChanged
    ? context.episode?.careManager?.display ??
      careManagerId ??
      careManagerRef?.replace('Practitioner/', '')
    : undefined;

  const isComplete = stage === 'fulfilled' || careManagerChanged;

  let statusLabel = 'Pending Transfer';
  if (isComplete) {
    statusLabel = 'Transfer Completed';
  } else if (stage === 'consultation') {
    statusLabel = 'Pending Consultation';
  }

  const completedOn = isComplete
    ? formatChartDate(
        context.serviceRequest?.meta?.lastUpdated ??
          context.episode?.meta?.lastUpdated,
      )
    : undefined;

  return { statusLabel, isComplete, assignedProvider, completedOn };
}

export function buildTransferClinicalNoteSoap(
  context: TransferChartNoteContext,
): NonNullable<ClinicalNote['soapNote']> {
  const { transferReasons, transferExplanation, recommendedClinicians } =
    extractTransferQrItems(context.qr);
  const prefs = context.serviceRequest
    ? extractServiceRequestPreferences(context.serviceRequest)
    : undefined;
  const outcome = resolveTransferOutcome(context);
  const stage = context.serviceRequest ? getTrackerStage(context.serviceRequest) : undefined;

  const subjectiveLines: string[] = [];
  if (transferReasons.length > 0) {
    subjectiveLines.push(
      `Transfer reason(s): ${transferReasons.map(reasonLabel).join(', ')}`,
    );
  }
  if (transferExplanation?.trim()) {
    subjectiveLines.push('', 'Explanation:', transferExplanation.trim());
  }
  if (recommendedClinicians?.trim()) {
    subjectiveLines.push('', 'Recommended clinician(s):', recommendedClinicians.trim());
  }

  const objectiveLines: string[] = [];
  if (prefs?.sessionFormat) {
    objectiveLines.push(`Session format: ${prefs.sessionFormat}`);
  }
  if (prefs?.areasOfFocus?.length) {
    objectiveLines.push(`Areas of focus: ${prefs.areasOfFocus.join(', ')}`);
  }
  if (prefs?.providerPreferences?.length) {
    objectiveLines.push(`Provider preferences: ${prefs.providerPreferences.join(', ')}`);
  }
  if (prefs?.availability?.length) {
    objectiveLines.push(`Availability: ${prefs.availability.join(', ')}`);
  }

  const outgoingProvider =
    context.qr.author?.display ??
    practitionerIdFromRef(context.qr.author?.reference) ??
    '—';

  const assessmentLines = [`Status: ${outcome.statusLabel}`, `From provider: ${outgoingProvider}`];
  if (context.episodeDisplay) {
    assessmentLines.push(`Episode: ${context.episodeDisplay}`);
  }
  if (outcome.assignedProvider) {
    assessmentLines.push(`Assigned provider: ${outcome.assignedProvider}`);
  }
  if (outcome.completedOn) {
    assessmentLines.push(`Completed: ${outcome.completedOn}`);
  }

  let plan: string;
  if (outcome.isComplete && outcome.assignedProvider) {
    plan = `Client transferred to ${outcome.assignedProvider}. Continuing care under the new provider panel.`;
  } else if (outcome.isComplete) {
    plan = 'Transfer completed. Continuing care under the assigned provider panel.';
  } else if (stage === 'consultation') {
    plan = 'Awaiting consultation outcome before final provider assignment.';
  } else {
    plan = 'Awaiting provider assignment through the transfers portal.';
  }

  return {
    subjective: subjectiveLines.join('\n').trim() || 'Transfer request submitted.',
    objective: objectiveLines.join('\n').trim() || 'No matching preferences recorded.',
    assessment: assessmentLines.join('\n'),
    plan,
  };
}

export function transferServiceRenderedLabel(
  context: TransferChartNoteContext,
): string {
  const outcome = resolveTransferOutcome(context);

  if (outcome.isComplete && outcome.assignedProvider) {
    return `Transfer completed — assigned to ${outcome.assignedProvider}`;
  }
  if (outcome.isComplete) {
    return 'Transfer completed';
  }
  const stage = context.serviceRequest ? getTrackerStage(context.serviceRequest) : undefined;
  if (stage === 'consultation') {
    return 'Transfer request — pending consultation';
  }
  return 'Transfer request — pending assignment';
}

export function transferChartNoteStatus(
  context: TransferChartNoteContext,
): { status: string; noteStatus: ClinicalNote['noteStatus'] } {
  const outcome = resolveTransferOutcome(context);
  if (outcome.isComplete) {
    return { status: 'Complete', noteStatus: 'Complete' };
  }
  return { status: 'Pending', noteStatus: 'Complete' };
}
